import { useEffect } from 'react'
import type { VoiceState } from '../hooks/useVoice'

interface VoiceButtonProps {
  state: VoiceState
  onStart: () => void
  onStop: () => void
  /** Compact mode: shrinks the button to a small FAB (~64px) without rings. */
  compact?: boolean
  /**
   * Still mode: kills the idle pulse rings + breathing scale. Used on the
   * desktop hero where the surrounding composition is editorial and the
   * pulse animations read as "bouncing" rather than "inviting." Per Ryan
   * 2026-05-06: "the left side needs to stop moving."
   */
  still?: boolean
}

export default function VoiceButton({ state, onStart, onStop, compact = false, still = false }: VoiceButtonProps) {
  // Keyboard: Space to toggle recording (skip if user is typing in an input)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      e.preventDefault()

      if (state === 'idle') onStart()
      else if (state === 'recording') onStop()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [state, onStart, onStop])

  const handleClick = () => {
    try { navigator.vibrate?.(10) } catch {}
    if (state === 'idle') onStart()
    else if (state === 'recording') onStop()
  }

  // Design B: voice button is the page's gravity well in default size.
  // Compact mode: shrinks to a tight ~50px FAB next to the text input
  // pill — icon-only, no label. Smaller than the previous 84px so it
  // matches the input pill height and doesn't dominate the dock.
  //
  // `still` mode (desktop) reduces the max so the disc reads as a
  // calm secondary affordance next to the now-visible text-input pill,
  // not a 360px focal weight that out-shouts the editorial composition.
  // Per round-2 critic E2: "the gold disc is calmer but still the
  // loudest object on the page — it can drop to ~85% size."
  const btnSize = compact
    ? '50px'
    : still
      ? 'clamp(160px, calc(110px + 11vw), 280px)'
      : 'clamp(180px, calc(120px + 13vw), 360px)'
  const ringSize = compact
    ? '64px'
    : still
      ? 'clamp(200px, calc(140px + 13vw), 320px)'
      : 'clamp(220px, calc(150px + 16vw), 440px)'

  return (
    <div className="relative flex items-center justify-center">
      {/* Pulse rings (idle) — filled for visibility on light backgrounds.
          Compact mode keeps a single soft ring so the small button still
          breathes (matches the screenshot's outer halo). Suppressed when
          `still` is true (desktop hero) so the editorial composition
          doesn't bounce. */}
      {state === 'idle' && !still && compact && (
        <span
          className="absolute rounded-full pointer-events-none"
          style={{
            width: ringSize,
            height: ringSize,
            background: 'var(--accent-glow)',
            animation: 'pulse-ring 3s ease-out infinite',
          }}
        />
      )}
      {state === 'idle' && !still && !compact && (
        <>
          <span
            className="absolute rounded-full pointer-events-none"
            style={{
              width: ringSize,
              height: ringSize,
              background: 'var(--accent-glow)',
              animation: 'pulse-ring 3s ease-out infinite',
            }}
          />
          <span
            className="absolute rounded-full pointer-events-none"
            style={{
              width: ringSize,
              height: ringSize,
              background: 'var(--accent-muted)',
              animation: 'pulse-ring 3s ease-out infinite 1.5s',
            }}
          />
        </>
      )}

      {/* Recording state — single slow halo ring. The previous version
          layered two rings at 2.4s + 1.5s, which read as frenetic. One
          ring at 4s + the calmer button breathing keyframe gives a
          confident "we're listening" rhythm without urgency. */}
      {state === 'recording' && !compact && (
        <span
          className="absolute rounded-full pointer-events-none"
          style={{
            width: ringSize,
            height: ringSize,
            background: 'var(--recording-glow)',
            animation: 'pulse-ring 4s ease-out infinite',
          }}
        />
      )}

      {/* Main button */}
      <button
        onClick={handleClick}
        disabled={state === 'processing'}
        className={`
          relative z-10 flex items-center justify-center rounded-full
          select-none cursor-pointer
          ease-out
          active:scale-[0.92]
          ${
            state === 'idle' && !compact && !still
              ? 'animate-pulse-gentle'
              : state === 'recording' && !compact
                ? 'animate-recording-pulse'
                : ''
          }
          ${state === 'processing' ? 'opacity-70 cursor-wait' : ''}
        `}
        style={{
          width: btnSize,
          height: btnSize,
          // 280ms transition matches the dock's keyboard slide-up so they
          // animate together as one motion when toggling text mode.
          transition: 'width 280ms cubic-bezier(0.4, 0, 0.2, 1), height 280ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms ease-out',
          // Color polarity flipped (per Ryan): the tap-to-talk button
          // is the darker green; the Report/Status tab pill is the
          // lighter wash.
          //
          // Processing: a soft green-tinted wash (was a beige cream
          // gradient that read as "broken" and off-brand). Stays in
          // the same hue family as idle/recording — same green, just
          // much lighter — so the button always reads as part of the
          // brand, even mid-think.
          background: state === 'idle'
            ? 'var(--accent)'
            : state === 'recording'
              ? 'var(--accent-hover)'
              : 'rgba(0, 103, 71, 0.18)',
        }}
        aria-label={
          state === 'idle'
            ? 'Tap to talk'
            : state === 'recording'
              ? 'Tap to send'
              : 'Processing...'
        }
      >
        {state === 'idle' && !compact && (
          <div className="flex flex-col items-center gap-2">
            <MicIcon size={42} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-on-accent)', letterSpacing: '0.01em' }}>
              tap to talk
            </span>
          </div>
        )}

        {state === 'recording' && !compact && (
          <div className="flex flex-col items-center gap-3">
            <Waveform />
            <span className="text-sm font-medium" style={{ color: 'var(--text-on-accent)', letterSpacing: '0.01em' }}>
              tap when done
            </span>
          </div>
        )}

        {state === 'processing' && !compact && (
          <div className="flex flex-col items-center gap-3">
            <Spinner />
            <span style={{
              color: 'var(--ink-2)',
              fontFamily: 'var(--font-sans)',
              fontStyle: 'italic',
              fontSize: '14px',
              fontWeight: 400,
            }}>
              thinking…
            </span>
          </div>
        )}

        {/* Compact: icon-only at ~50px, no label. */}
        {compact && state === 'idle' && <MicIcon size={20} />}
        {compact && state === 'recording' && (
          <div className="flex items-center gap-0.5" style={{ height: 16 }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-0.5 rounded-full"
                style={{
                  background: 'var(--text-on-accent)',
                  opacity: 0.9,
                  animation: 'waveform 1.6s ease-in-out infinite',
                  animationDelay: `${i * 0.18}s`,
                  minHeight: 6,
                }}
              />
            ))}
          </div>
        )}
        {compact && state === 'processing' && (
          <div className="flex items-center gap-0.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1 h-1 rounded-full"
                style={{
                  background: 'var(--ink-3)',
                  opacity: 0.45,
                  animation: 'thinking-dot 1.4s ease-in-out infinite',
                  animationDelay: `${i * 0.18}s`,
                }}
              />
            ))}
          </div>
        )}
      </button>
    </div>
  )
}

function MicIcon({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: 'var(--text-on-accent)' }}
    >
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  )
}

function Waveform() {
  return (
    <div className="flex items-center gap-1" style={{ height: 32 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-1 rounded-full"
          style={{
            background: 'var(--text-on-accent)',
            opacity: 0.9,
            animation: `waveform 1.6s ease-in-out infinite`,
            animationDelay: `${i * 0.22}s`,
            minHeight: 8,
          }}
        />
      ))}
    </div>
  )
}

function Spinner() {
  // Three sequentially-pulsing dots — skeleton/progress-style affordance reads
  // ~30% faster than a spinning circle. Replaces the previous SVG spinner that
  // signaled nothing about progress. (See latency audit, win #4.)
  return (
    <div className="flex items-center gap-1.5" style={{ height: 28 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 rounded-full"
          style={{
            background: 'var(--ink-3)',
            opacity: 0.35,
            animation: 'thinking-dot 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </div>
  )
}
